import { guid, UTC, DateRange } from 'rewire-common';
import { observable, freeze, defaultEquals } from 'rewire-core';
import { ICell, IColumn, IGrid, IGridOptions, IRowData, createGrid, createColumn, IColumnOptions } from 'rewire-grid';
import { numberColumn, ITimecardTest, ITimecard, ITemplate, MultipleType, ReasonCategories, isMultiple } from '../DataTypes';
import { formatTime, makeShortCellDate } from '../utility/DateUtils';
import { objectKeys } from '../utility/Object';
import {
  nameMapper,
  codeMapper,
  numberStringMapper,
  getDifferenceMapper,
  getDifferenceCellTooltip,
} from '../utility/DataConverters';
import { StatusIconRenderer } from './TestCellRenderers';
import DifferenceCellRenderer from '../ui/DifferenceCellRenderer';

import TimecardTestSummaryRolledUpTimecard, { IRollupReason } from './TimecardTestSummaryRolledUpTimecard';

export interface IRolledUpTestTimecard {
  id: string;
  status?: string;
  earnedDate: UTC;
  start?: UTC;
  end?: UTC;
  units?: number;
  rate?: number | MultipleType;
  gross: number;
  accountCode?: string | MultipleType;
  template?: ITemplate | MultipleType;
  otherPay?: number;
  reasons: IRollupReason[];
  timecards: ITimecard[];
  otherPayments: ITimecard[];
}

interface ITimecardTestSummaryDailyRollupModelState {
  grid: IGrid;
  test?: ITimecardTest;
  rolledUpOriginalTimecards: IRolledUpTestTimecard[];
  rolledUpActualTimecards: IRolledUpTestTimecard[];
}

export class TimecardTestSummaryDailyRollupModel {
  state: ITimecardTestSummaryDailyRollupModelState;
  DYNAMIC_COLUMN_STARTING_POSITION: number = 11;

  constructor() {
    this.state = observable({
      grid: {} as IGrid,
      test: undefined,
      rolledUpOriginalTimecards: [],
      rolledUpActualTimecards: [],
    });

    this.grid = this.createGrid([], []);
  }

  reset() {
    freeze(() => {
      this.test = undefined;
      this.rolledUpOriginalTimecards.length = 0;
      this.rolledUpActualTimecards.length = 0;
      this.grid.set([]);
    });
  }

  async load(test?: ITimecardTest) {
    this.reset();
    this.test = test;
    try {
      if (this.test) {
        await this.createRollups();
      }
      this.grid = this.createGrid(this.rolledUpOriginalTimecards, this.rolledUpActualTimecards);
    } catch (err) {
      this.reset();
    }
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  set grid(grid: IGrid) {
    this.state.grid = grid;
  }

  get test(): ITimecardTest | undefined {
    return this.state.test;
  }

  set test(value: ITimecardTest | undefined) {
    this.state.test = value;
  }

  get effective(): DateRange | undefined {
    return this.test?.effective;
  }

  get originalTimecards(): ITimecard[] | undefined {
    return this.test?.originalTimecards;
  }

  get actualTimecards(): ITimecard[] | undefined {
    return this.test?.actualTimecards;
  }

  get rolledUpOriginalTimecards(): IRolledUpTestTimecard[] {
    return this.state.rolledUpOriginalTimecards;
  }

  get rolledUpActualTimecards(): IRolledUpTestTimecard[] {
    return this.state.rolledUpActualTimecards;
  }

  private isTimecard(timecard: ITimecard) {
    // it's a timecard if:
    //    1. there's no reason specified
    //    2. the reason has no category (not likely)
    //    3. the reason's category is NOT 'other'
    return !timecard.reason?.category || timecard.reason.category !== ReasonCategories.Other;
  }

  private *getRange(): Iterable<UTC> {
    for (const earnedDate of this.effective!) {
      yield earnedDate;
    }
  }

  private async createRollups() {
    const rolledUpOriginalTimecards: IRolledUpTestTimecard[] = [];
    const rolledUpActualTimecards: IRolledUpTestTimecard[] = [];

    for (const earnedDate of this.getRange()) {
      rolledUpOriginalTimecards.push(this.createRollup(earnedDate, this.originalTimecards));
      rolledUpActualTimecards.push(this.createRollup(earnedDate, this.actualTimecards));
    }

    this.rolledUpOriginalTimecards.set(rolledUpOriginalTimecards);
    this.rolledUpActualTimecards.set(rolledUpActualTimecards);
  }

  private createRollup(earnedDate: UTC, timecards?: ITimecard[]): IRolledUpTestTimecard {
    const alltimecardsForEarnedDate: ITimecard[] = timecards?.filter((t) => earnedDate.equals(t.earnedDate)) ?? [];
    const timecardsForEarnedDate: ITimecard[] = [];
    const otherPaymentsForEarnedDate: ITimecard[] = [];
    for (const timecard of alltimecardsForEarnedDate) {
      if (this.isTimecard(timecard)) {
        timecard.start = timecard.effective?.start;
        timecard.end = timecard.effective?.end;
        timecardsForEarnedDate.push(timecard);
      } else {
        otherPaymentsForEarnedDate.push(timecard);
      }
    }
    const rolledUpTimecard: IRolledUpTestTimecard = TimecardTestSummaryRolledUpTimecard.create(
      earnedDate,
      timecardsForEarnedDate,
      otherPaymentsForEarnedDate,
    );
    return rolledUpTimecard;
  }

  private get columns(): any[] {
    return [
      { name: 'status', label: '', type: 'none', width: '35px', fixed: true, renderer: StatusIconRenderer },
      { name: 'earnedDate', label: 'Date', type: 'none', width: '75px', map: makeShortCellDate, fixed: true },
      {
        name: 'group',
        label: 'Group',
        type: 'none',
        width: '125px',
        map: getDifferenceMapper(nameMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'template',
        label: 'Shift Type',
        type: 'none',
        width: '125px',
        map: getDifferenceMapper(codeMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'start',
        label: 'Start Time',
        type: 'none',
        width: '80px',
        map: getDifferenceMapper(formatTime),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'end',
        label: 'End Time',
        type: 'none',
        width: '80px',
        map: getDifferenceMapper(formatTime),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'units',
        label: 'Units',
        type: numberColumn,
        width: '80px',
        map: getDifferenceMapper(numberStringMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'reason',
        label: 'Reason',
        type: 'none',
        width: '125px',
        map: getDifferenceMapper(nameMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'occupation',
        label: 'Occupation',
        type: 'none',
        width: '125px',
        map: getDifferenceMapper(nameMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'rate',
        label: 'Rate',
        type: 'none',
        width: '80px',
        align: 'right',
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'accountCode',
        label: 'Account Code',
        type: 'none',
        width: '80px',
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'gross',
        label: 'Gross',
        type: numberColumn,
        width: '80px',
        map: getDifferenceMapper(numberStringMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
      {
        name: 'otherPay',
        label: 'Other Pay',
        type: numberColumn,
        width: '80px',
        map: getDifferenceMapper(numberStringMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      },
    ];
  }

  private get reasonColumnNames(): string[] {
    const reasons: IRollupReason[] = [];

    for (const timecard of this.rolledUpOriginalTimecards) {
      for (const reason of timecard.reasons) {
        if (!reasons.map((r) => r.name).includes(reason.name)) {
          reasons.push(reason);
        }
      }
    }
    for (const timecard of this.rolledUpActualTimecards) {
      for (const reason of timecard.reasons) {
        if (!reasons.map((r) => r.name).includes(reason.name)) {
          reasons.push(reason);
        }
      }
    }
    return reasons.sort(this.orderByCategory).map((r) => r.name);
  }

  private orderByCategory(reason1: IRollupReason, reason2: IRollupReason): number {
    // if categories match, order by name
    if (reason1.columnOrder === reason2.columnOrder) {
      return reason1.name < reason2.name ? -1 : 1;
    }
    return reason1.columnOrder - reason2.columnOrder;
  }

  private areRollupReasonsEqual(originalRollupReasons: IRollupReason[], actualRollupReasons: IRollupReason[]): boolean {
    if (originalRollupReasons.length !== actualRollupReasons.length) {
      return false;
    }

    for (const originalRollupReason of originalRollupReasons) {
      const matchingActualRollupReason = actualRollupReasons.find((ar) => ar.name === originalRollupReason.name);
      if (!matchingActualRollupReason || originalRollupReason.amount !== matchingActualRollupReason.amount) {
        return false;
      }
    }

    return true;
  }

  private areRollupRowsEqual(originalRow: IRolledUpTestTimecard, actualRow: IRolledUpTestTimecard): boolean {
    let rowsEqual = true;

    for (const key of objectKeys(originalRow)) {
      const originalValue = originalRow[key];
      const actualValue = actualRow[key];
      if (key === 'id' || key === 'earnedDate' || key === 'timecards' || key === 'otherPayments') {
        continue;
      }
      if (key === 'reasons') {
        const reasonsEqual = this.areRollupReasonsEqual(originalValue as IRollupReason[], actualValue as IRollupReason[]);
        if (!reasonsEqual) {
          rowsEqual = false;
          break;
        }
      } else {
        if (!((isMultiple(originalValue) && isMultiple(actualValue)) || defaultEquals(originalValue, actualValue))) {
          rowsEqual = false;
          break;
        }
      }
    }

    return rowsEqual;
  }

  private createGrid(rolledUpOriginalTimecards: IRolledUpTestTimecard[], rolledUpActualTimecards: IRolledUpTestTimecard[]): IGrid {
    const cols: IColumn[] = this.createColumns();
    const rows: IRowData[] = this.createRows(rolledUpOriginalTimecards, rolledUpActualTimecards, cols);
    const options: IGridOptions = this.createOptions();
    const grid: IGrid = createGrid(rows, cols, options);
    grid.rowHeight = 32;
    return grid;
  }

  private createOptions(): IGridOptions {
    return {
      readOnly: true,
      clearSelectionOnBlur: true,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      },
    };
  }

  private createColumns(): IColumn[] {
    const cols: IColumn[] = [];

    for (const col of this.columns) {
      const { name, label, ...opts } = col;
      opts.canSort = false;
      const column = createColumn(name, label, opts);
      cols.push(column);
    }

    this.reasonColumnNames.forEach((col: any, index: number) => {
      // something smells weird about the type parameter for this!!
      const opts = {
        type: numberColumn,
        width: '80px',
        map: getDifferenceMapper(numberStringMapper),
        cellTooltip: getDifferenceCellTooltip,
        renderer: DifferenceCellRenderer,
      } as unknown as IColumnOptions;
      const column = createColumn(col, col, opts);
      column.align = 'right';
      column.canSort = false;
      cols.splice(index + this.DYNAMIC_COLUMN_STARTING_POSITION, 0, column);
    });
    return cols;
  }

  private createRows(
    rolledUpOriginalTimecards: IRolledUpTestTimecard[],
    rolledUpActualTimecards: IRolledUpTestTimecard[],
    cols: IColumn[],
  ): IRowData[] {
    const rows: IRowData[] = [];

    for (let i = 0; i < rolledUpOriginalTimecards.length; i++) {
      const r: IRowData = { id: `${guid()}`, data: {} };
      const rolledUpOriginalRow = rolledUpOriginalTimecards[i];
      const rolledUpActualRow = rolledUpActualTimecards[i];
      for (const col of cols) {
        let v: any;
        const fieldName = col.name as keyof IRolledUpTestTimecard;
        if (fieldName === 'status') {
          v = this.areRollupRowsEqual(rolledUpOriginalRow, rolledUpActualRow) ? 'ok' : 'error';
        } else if (fieldName === 'earnedDate') {
          v = rolledUpOriginalTimecards[i].earnedDate;
        } else {
          v = { value1: undefined, value2: undefined };
          const originalReasonData = rolledUpOriginalRow.reasons.find((reason: IRollupReason) => reason.name === fieldName);
          const actualReasonData = rolledUpActualRow.reasons.find((reason: IRollupReason) => reason.name === fieldName);
          if (originalReasonData) {
            v.value1 = originalReasonData.units;
          } else {
            v.value1 = rolledUpOriginalTimecards[i][fieldName];
          }
          if (actualReasonData) {
            v.value2 = actualReasonData.units;
          } else {
            v.value2 = rolledUpActualTimecards[i][fieldName];
          }
        }
        r.data![fieldName] = v;
      }
      rows.push(r);
    }
    return rows;
  }
}

const timecardTestSummaryDailyRollupModel = new TimecardTestSummaryDailyRollupModel();
export default timecardTestSummaryDailyRollupModel;
